import CloseButton from 'Components/Common/CloseButton';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { Offcanvas, OffcanvasBody } from 'reactstrap';
import { searchCampaigns } from 'services/api/campaign';
import { RootState } from 'store';
import { ContentLibraryFilters, defaultContentLibraryFilters } from 'types/campaign/content';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';

interface ContentLibraryFiltersModalProps {
    isOpen: boolean;
    onClose: () => void;
    defaultFilters: ContentLibraryFilters;
    onApply: (filters: ContentLibraryFilters) => void;
}

const ContentLibraryFiltersModal: React.FC<ContentLibraryFiltersModalProps> = ({
    isOpen,
    onClose,
    defaultFilters,
    onApply,
}) => {
    const [filters, setFilters] = useState<ContentLibraryFilters>(defaultContentLibraryFilters);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [flatpickr, setFlatPickr] = useState<boolean>(true)
    useEffect(() => {
        setFilters(defaultFilters);
    }, [defaultFilters]);

    const handleFilterChange = (key: string, value: any) => {
        setFilters({ ...filters, [key]: value });
    };

    const _loadCampaignsSuggestions = (query: string, callback: any, agency_id: any) => {
        if (!query || query.length < 3) return callback([]);
        searchCampaigns(query, agency_id)
            .then((resp) =>
                callback(resp.map((item: any) => ({ value: item.id, label: item.name })))
            )
            .catch(() => callback([]));
    };

    const loadCampaignsSuggestions = debounce(
        (query, callback) => _loadCampaignsSuggestions(query, callback, selectedAgencyId),
        200
    );

    const getSelectedDate = () => {
        const { start_date, end_date } = filters;
        const dates: Date[] = [];
        if (start_date) dates.push(moment(start_date, 'YYYY-MM-DD').toDate());
        if (end_date) dates.push(moment(end_date, 'YYYY-MM-DD').toDate());
        return dates;
    };

    const handleDateChange = (selectedDates: Date[]) => {
        if (selectedDates.length === 2) {
            setFilters({ ...filters, start_date: moment(selectedDates[0]).format('YYYY-MM-DD'), end_date: moment(selectedDates[1]).format('YYYY-MM-DD') })
        } else if (selectedDates.length === 1) {
            handleFilterChange('end_date', '');
        } else {
            setFilters({ ...filters, start_date: '', end_date: '' })
        }
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction="end">
            <CloseButton top="10px" onClose={onClose} />
            <OffcanvasBody className="p-0 position-relative">
                <div className="payments-offcanvas-bg"></div>
                <div className="filters p-4">
                    {/* Campaign Filter */}
                    <div className="form-group filter-item">
                        <span className="filter-title">Campaign</span>
                        <AsyncSelect
                            value={typeof filters.campaign === 'string' ? null : filters.campaign ?? null}
                            onChange={(val) => {
                                setFilters({
                                    ...filters,
                                    campaign: val ?? null,
                                    campaign_id: val?.value,
                                });
                            }}
                            placeholder="Choose Campaign"
                            menuPlacement="bottom"
                            cacheOptions
                            className=""
                            isClearable
                            loadOptions={loadCampaignsSuggestions}
                        />
                    </div>

                    {/* Posted Between */}
                    <div className="form-group filter-item">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="filter-title">Posted Between</span>
                            {
                                (filters.start_date || filters.end_date) &&
                                <button onClick={() => {
                                    handleDateChange([])
                                    setFlatPickr(false);
                                    setTimeout(() => {
                                        setFlatPickr(true)
                                    }, 0)
                                }} className="unset-all text-primary cursor-pointer" style={{ fontSize: '12px' }}>Clear</button>
                            }
                        </div>
                        {
                            flatpickr &&
                            <Flatpickr
                                className="border-0 dash-filter-picker filter-date-range performance-date-range-picker form-control"
                                options={{
                                    mode: 'range',
                                    dateFormat: 'd M, Y',
                                }}
                                {...({ placeholder: "Select Date Range" } as any)}
                                value={getSelectedDate() as unknown as Date}
                                onChange={handleDateChange}
                            />
                        }
                    </div>
                </div>

                {/* Footer */}
                <div className="filters-offcanvas-footer d-flex justify-content-between align-items-center px-3 p-4 gap-2">
                    <button
                        className="theme-btn theme-btn-lg w-100 btn-primary-outlined"
                        onClick={() => {
                            onApply(defaultContentLibraryFilters);
                            onClose();
                        }}
                    >
                        Reset
                    </button>
                    <button
                        className="theme-btn theme-btn-lg w-100 btn-primary"
                        onClick={() => {
                            onApply(filters);
                            onClose();
                        }}
                    >
                        Apply
                    </button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default ContentLibraryFiltersModal;
